// @generated-imports
import { applyFiltersAndRender, buildAbsoluteUrl, escapeAttribute, escapeHTML, findGameById, showToast, truncate } from './app.js';
import { updateGameModalExtras } from './collections.js';
import { CONFIG, dom, state } from './core.js';
import { armButtonConfirm } from './datapanel.js';
import { formatBytes } from './format.js';
import { tryPersistStorage } from './lite.js';
import { bumpCounter, evaluateAchievements } from './stats.js';
// @generated-imports
/* ===========================================================================
   OFFLINE POR JOGO (v2.1)
   ---------------------------------------------------------------------------
   "Disponível offline" guarda a ROM (e capa/screenshot) no cache
   `emutab-offline-v1`, servido pelo service worker com cache-first. O
   emulador (loader, scripts e núcleos da build) é guardado pelo SW em
   `emutab-emu-v1` com rede-primeiro sempre que é carregado online — então
   um sistema fica pronto offline depois de abrir QUALQUER jogo dele uma vez
   com internet nesta build. O status é honesto:
     ready       → ROM guardada e núcleo já carregado online nesta build
     needs-core  → ROM guardada, falta abrir um jogo deste sistema online
   Índice em `emutab:offline-games` (do aparelho, não do perfil).
   ======================================================================== */

const OFFLINE_CACHE = 'emutab-offline-v1';
const EMU_RUNTIME_CACHE = 'emutab-emu-v1';

function offlineCacheAvailable() {
  return typeof caches !== 'undefined' && caches && typeof caches.open === 'function';
}

function offlineKey(url) {
  const u = new URL(url, window.location.href);
  u.search = '';
  u.hash = '';
  return u.href;
}

function loadOfflineIndex() {
  try { return JSON.parse(localStorage.getItem(CONFIG.localStorageOfflineKey) || '{}') || {}; } catch (_) { return {}; }
}

function saveOfflineIndex(index) {
  try { localStorage.setItem(CONFIG.localStorageOfflineKey, JSON.stringify(index)); } catch (_) {}
}

function isGameOffline(game) {
  return Boolean(game && !game.isLocal && loadOfflineIndex()[game.id]);
}

async function isRomCachedOffline(url) {
  if (!url || !offlineCacheAvailable()) return false;
  try {
    const cache = await caches.open(OFFLINE_CACHE);
    return Boolean(await cache.match(offlineKey(url)));
  } catch (_) { return false; }
}

function loadCoresReady() {
  try { return JSON.parse(localStorage.getItem(CONFIG.localStorageCoresReadyKey) || '{}') || {}; } catch (_) { return {}; }
}

/** Chamado quando uma sessão chega a `playing` com rede: o SW guardou o núcleo. */
function markCoreReady(session) {
  if (session.kind === 'local') bumpCounter('localPlays');
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  const map = loadCoresReady();
  map[`${CONFIG.emulatorBuild}:${session.core}`] = Date.now();
  try { localStorage.setItem(CONFIG.localStorageCoresReadyKey, JSON.stringify(map)); } catch (_) {}
}

function isCoreReady(core) {
  return Boolean(loadCoresReady()[`${CONFIG.emulatorBuild}:${core}`]);
}

function offlineStatus(game) {
  if (!isGameOffline(game)) return 'none';
  return isCoreReady(game.core) ? 'ready' : 'needs-core';
}

function describeOfflineStatus(game) {
  const status = offlineStatus(game);
  if (status === 'ready') return '✈️ Disponível offline';
  if (status === 'needs-core') return `✈️ ROM guardada — abra um jogo de ${game.system} com internet uma vez para concluir`;
  return '';
}

async function makeGameOffline(game) {
  if (!game || game.isLocal) return false;
  if (!offlineCacheAvailable()) { showToast('Este navegador não permite guardar jogos offline.'); return false; }
  if (navigator.onLine === false) { showToast('Conecte-se à internet para baixar o jogo.'); return false; }
  showToast(`Baixando "${truncate(game.title, 30)}" para jogar offline…`);
  try {
    const cache = await caches.open(OFFLINE_CACHE);
    const romUrl = buildAbsoluteUrl(game.romUrl);
    const response = await fetch(romUrl, { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    await cache.put(offlineKey(romUrl), new Response(blob, { headers: { 'Content-Type': blob.type || 'application/octet-stream', 'Content-Length': String(blob.size) } }));
    let total = blob.size;
    for (const asset of [game.cover, game.screenshot].filter(Boolean)) {
      try {
        const assetUrl = buildAbsoluteUrl(asset);
        // eslint-disable-next-line no-await-in-loop
        const res = await fetch(assetUrl);
        if (res.ok) {
          // eslint-disable-next-line no-await-in-loop
          const assetBlob = await res.blob();
          // eslint-disable-next-line no-await-in-loop
          await cache.put(offlineKey(assetUrl), new Response(assetBlob, { headers: { 'Content-Type': assetBlob.type || 'image/jpeg' } }));
          total += assetBlob.size;
        }
      } catch (_) { /* capa é opcional */ }
    }
    const index = loadOfflineIndex();
    index[game.id] = { bytes: total, at: Date.now(), romUrl, core: game.core, build: CONFIG.emulatorBuild };
    saveOfflineIndex(index);
    tryPersistStorage();
    bumpCounter('offlineGames');
    applyFiltersAndRender();
    if (state.modalGame && state.modalGame.id === game.id) updateGameModalExtras(game);
    renderOfflineSection();
    evaluateAchievements();
    showToast(offlineStatus(game) === 'ready'
      ? `✈️ "${truncate(game.title, 30)}" pronto para jogar offline (${formatBytes(total)}).`
      : `ROM de "${truncate(game.title, 30)}" guardada (${formatBytes(total)}). Abra um jogo de ${game.system} uma vez com internet para o emulador também ficar offline.`);
    return true;
  } catch (error) {
    showToast(`Não deu para guardar "${truncate(game.title, 30)}" offline: ${(error && error.message) || 'erro'}.`);
    return false;
  }
}

async function removeGameOffline(gameId) {
  const index = loadOfflineIndex();
  const entry = index[gameId];
  if (!entry) return false;
  delete index[gameId];
  saveOfflineIndex(index);
  if (offlineCacheAvailable()) {
    try {
      const cache = await caches.open(OFFLINE_CACHE);
      await cache.delete(offlineKey(entry.romUrl));
      const game = findGameById(gameId);
      if (game) {
        for (const asset of [game.cover, game.screenshot].filter(Boolean)) {
          // eslint-disable-next-line no-await-in-loop
          await cache.delete(offlineKey(buildAbsoluteUrl(asset)));
        }
      }
    } catch (_) { /* noop */ }
  }
  applyFiltersAndRender();
  renderOfflineSection();
  return true;
}

async function removeAllOffline() {
  saveOfflineIndex({});
  try { localStorage.removeItem(CONFIG.localStorageCoresReadyKey); } catch (_) {}
  if (offlineCacheAvailable()) {
    try { await caches.delete(OFFLINE_CACHE); await caches.delete(EMU_RUNTIME_CACHE); } catch (_) {}
  }
  applyFiltersAndRender();
  renderOfflineSection();
}

/** Boot: descarta entradas cuja ROM sumiu do cache (limpeza do navegador). */
async function verifyOfflineIndex() {
  const index = loadOfflineIndex();
  const ids = Object.keys(index);
  if (!ids.length || !offlineCacheAvailable()) return 0;
  let dropped = 0;
  for (const id of ids) {
    // eslint-disable-next-line no-await-in-loop
    if (!(await isRomCachedOffline(index[id].romUrl))) { delete index[id]; dropped += 1; }
  }
  if (dropped) saveOfflineIndex(index);
  return dropped;
}

function offlineTotalBytes() {
  return Object.values(loadOfflineIndex()).reduce((sum, entry) => sum + (Number(entry.bytes) || 0), 0);
}

async function toggleGameOffline(game) {
  if (isGameOffline(game)) {
    await removeGameOffline(game.id);
    showToast(`"${truncate(game.title, 30)}" não fica mais offline.`);
    if (state.modalGame && state.modalGame.id === game.id) updateGameModalExtras(game);
    return false;
  }
  return makeGameOffline(game);
}

function renderOfflineSection() {
  if (!dom.offlineList) return;
  const index = loadOfflineIndex();
  const ids = Object.keys(index);
  if (!ids.length) {
    dom.offlineList.innerHTML = '<div class="backup-row">Nenhum jogo offline. Abra os detalhes de um jogo do catálogo e toque em "✈️ Disponível offline".</div>';
    return;
  }
  dom.offlineList.innerHTML = ids.map((id) => {
    const game = findGameById(id);
    const title = game ? game.title : id;
    const status = game ? (offlineStatus(game) === 'ready' ? 'pronto' : 'falta o núcleo') : '';
    return `<div class="backup-row data-row">
      <span class="data-info">${escapeHTML(truncate(title, 34))} <small>${formatBytes(index[id].bytes || 0)}${status ? ` • ${status}` : ''}</small></span>
      <span class="data-actions"><button class="mini-btn mini-btn-danger" type="button" data-offline-remove="${escapeAttribute(id)}">Remover</button></span>
    </div>`;
  }).join('') + `<div class="backup-row data-row"><span class="data-info"><small>Total: ${formatBytes(offlineTotalBytes())}</small></span>
    <span class="data-actions"><button class="mini-btn mini-btn-danger" type="button" data-offline-clear="1">Remover todos</button></span></div>`;
}

async function handleOfflineListClick(event) {
  const remove = event.target.closest('[data-offline-remove]');
  if (remove) { await removeGameOffline(remove.dataset.offlineRemove); return; }
  const clear = event.target.closest('[data-offline-clear]');
  if (clear) {
    if (!armButtonConfirm(clear)) return;
    await removeAllOffline();
    showToast('Jogos offline removidos.');
  }
}

// @generated-exports
export { EMU_RUNTIME_CACHE, OFFLINE_CACHE, describeOfflineStatus, handleOfflineListClick, isCoreReady, isGameOffline, isRomCachedOffline, loadCoresReady, loadOfflineIndex, makeGameOffline, markCoreReady, offlineCacheAvailable, offlineKey, offlineStatus, offlineTotalBytes, removeAllOffline, removeGameOffline, renderOfflineSection, saveOfflineIndex, toggleGameOffline, verifyOfflineIndex };
// @generated-exports
